import { createFileRoute } from '@tanstack/react-router'
import { StaticNav } from '@/components/static-nav'
import { StaticFooter } from '@/components/static-footer'
import { SignInButton } from '@/components/sign-in-button'
import {
  IconFolderOpen,
  IconSparkles,
  IconInbox,
  IconArchive,
  IconStar,
  IconFilter,
  IconTags,
  IconStack2,
  IconCheck,
  IconArrowRight
} from '@tabler/icons-react'

export const Route = createFileRoute('/email-organization')({
  component: EmailOrganizationPage,
})

function EmailOrganizationPage() {
  const systems = [
    {
      name: "Inbox Zero Method",
      description: "Process every email to zero by day's end",
      color: "blue",
      actions: [
        { action: "Delete", desc: "Not needed" },
        { action: "Delegate", desc: "Forward to others" },
        { action: "Respond", desc: "Under 2 minutes" },
        { action: "Defer", desc: "Add to task list" },
        { action: "Archive", desc: "Keep for reference" }
      ]
    },
    {
      name: "PARA Method",
      description: "Organize by actionability",
      color: "purple",
      actions: [
        { action: "Projects", desc: "Active work with deadlines" },
        { action: "Areas", desc: "Ongoing responsibilities" },
        { action: "Resources", desc: "Reference material" },
        { action: "Archives", desc: "Inactive items" }
      ]
    },
    {
      name: "Two-Folder System",
      description: "Minimal complexity, maximum clarity",
      color: "green",
      actions: [
        { action: "Action Required", desc: "Needs response or action" },
        { action: "Archive", desc: "Everything else to keep" }
      ]
    }
  ]

  const gmailFeatures = [
    { icon: <IconTags className="w-5 h-5" />, title: "Labels", description: "Tag emails with multiple categories" },
    { icon: <IconStar className="w-5 h-5" />, title: "Stars", description: "Mark important emails for follow-up" },
    { icon: <IconFilter className="w-5 h-5" />, title: "Filters", description: "Automatically sort incoming mail" },
    { icon: <IconInbox className="w-5 h-5" />, title: "Priority Inbox", description: "Let Gmail surface important emails" },
  ]

  return (
    <div className="bg-[#fafafa] text-slate-950 min-h-screen">
      <StaticNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-purple-100/40 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-purple-50 border border-purple-100">
              <IconFolderOpen className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-700">
                Systems that actually work
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Email Organization:{' '}
              <span className="text-purple-600">Simple Systems</span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Most people either over-organize with dozens of unused folders or
              under-organize by leaving everything in the inbox. Find a simple
              system you'll actually maintain.
            </p>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-3xl p-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <IconStack2 className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">The Organization Paradox</h3>
                <p className="text-slate-600">
                  Creating complex folder structures often creates more work than it saves.
                  The best organization system is the simplest one that keeps your inbox actionable.
                  Focus on finding a system you'll actually use consistently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Organization Systems */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Popular Email Organization Systems
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Choose the system that fits your workflow
          </p>

          <div className="grid lg:grid-cols-3 gap-6">
            {systems.map((system, i) => {
              const colorClasses = {
                blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', badge: 'bg-blue-100' },
                purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', badge: 'bg-purple-100' },
                green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', badge: 'bg-green-100' },
              }[system.color] || { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', badge: 'bg-slate-100' }

              return (
                <div key={i} className={`${colorClasses.bg} ${colorClasses.border} border-2 rounded-3xl p-6`}>
                  <h3 className={`text-xl font-semibold ${colorClasses.text} mb-2`}>
                    {system.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-6">{system.description}</p>
                  <div className="space-y-3">
                    {system.actions.map((action, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <div className={`${colorClasses.badge} ${colorClasses.text} px-2 py-1 rounded text-xs font-medium min-w-[80px]`}>
                          {action.action}
                        </div>
                        <span className="text-sm text-slate-500">{action.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Gmail Features */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Gmail Organization Features
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Built-in tools to keep your inbox organized
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {gmailFeatures.map((feature, i) => (
              <div key={i} className="p-6 rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 text-center hover:border-purple-200 hover:bg-purple-50/50 transition-colors">
                <div className="w-12 h-12 rounded-3xl bg-purple-100 text-purple-600 flex items-center justify-center mx-auto mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl p-6">
                <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                  <div className="grid grid-cols-4 gap-px bg-slate-200">
                    {['Inbox', 'Archive', 'Delete', 'Unsubscribe'].map((cat, i) => (
                      <div key={i} className="bg-white p-3 text-center">
                        <div className="text-xs font-medium text-slate-500 mb-1">{cat}</div>
                        <div className="text-lg font-bold text-slate-900">{[42, 128, 89, 31][i]}</div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <IconCheck className="w-4 h-4" />
                      <span>290 emails automatically categorized</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                <IconSparkles className="w-4 h-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                AI-Powered Organization
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Tools like Winbox Zero use AI to automatically categorize your emails,
                saving you the mental effort of deciding where each email belongs.
                The Kanban-style interface makes it easy to process emails visually.
              </p>
              <ul className="space-y-3">
                {[
                  "Automatic categorization into 4 actions",
                  "Visual Kanban board for email processing",
                  "Learn from your preferences over time",
                  "Batch process similar emails together"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <IconCheck className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-[#fafafa] px-6 py-32">
        <div className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-8 py-20 shadow-2xl sm:px-16 sm:py-24 text-center">
            <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl mix-blend-screen" />
            <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-purple-500/20 blur-3xl mix-blend-screen" />
            
            <div className="relative z-10">
              <h2 className="mx-auto max-w-2xl text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                Organize your inbox with AI
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-slate-300">
                Get started today with unlimited email processing.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4">
                <SignInButton variant="dark" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <StaticFooter />
    </div>
  )
}
